// src/telas/minha-conta/index.tsx

import styles from "./styles.module.css";
import { Menu } from "../menu";
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { RxDotFilled } from "react-icons/rx";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { DadosBasicos } from "./dados-basicos";
import { Endereco } from "./endereco";
import { Contato } from "./contato";
import { Documentos } from "./documentos";
import { Financeiro } from "./financeiro";
import { CadastroType, ParceiroDefault } from "../../context/parceiro-cadastro";
import { AuthFetchJson, url } from "../../api";
import Swal from "sweetalert2";
import { ContextRegister } from "../../context";
import { Utils } from "../../utils";

type documentosType = {
  file: any;
  name: any;
};

export const FormularioContext = createContext<{
  formulario: CadastroType;
  setFormulario: Dispatch<SetStateAction<CadastroType>>;
} | null>(null);

export const ArquivosContext = createContext<{
  documentos: documentosType[];
  setDocumentos: Dispatch<SetStateAction<documentosType[]>>;
} | null>(null);

const TABS = [
  { key: "dadosbasicos", label: "Dados Básicos", step: 1 },
  { key: "endereco", label: "Endereço", step: 2 },
  { key: "contato", label: "Contato", step: 3 },
  { key: "financeiro", label: "Financeiro", step: 4 },
  { key: "documentos", label: "Documentos", step: 5 },
] as const;

type TabKey = (typeof TABS)[number]["key"];

export function MinhaConta() {
  const { state } = useContext(ContextRegister);

  const [tab, setTab] = useState<TabKey>("dadosbasicos");
  const [formulario, setFormulario] = useState(ParceiroDefault);
  const [documentos, setDocumentos] = useState<documentosType[]>([]);

  const currentStep = TABS.find((t) => t.key === tab)?.step ?? 1;

  /* ── Renderização da aba ────────────────────────────────── */
  function selectedTab() {
    switch (tab) {
      case "dadosbasicos":
        return <DadosBasicos />;
      case "endereco":
        return <Endereco />;
      case "contato":
        return <Contato />;
      case "financeiro":
        return <Financeiro />;
      case "documentos":
        return <Documentos />;
      default:
        return <DadosBasicos />;
    }
  }

  /* ── Navegação ──────────────────────────────────────────── */
  function handleBack() {
    const idx = TABS.findIndex((t) => t.key === tab);
    if (idx > 0) setTab(TABS[idx - 1].key);
  }

  async function handleNext() {
    const idx = TABS.findIndex((t) => t.key === tab);

    // Avança para a próxima aba
    if (idx < TABS.length - 1) {
      setTab(TABS[idx + 1].key);
      return;
    }

    // Última aba → submete
    let aux = { ...formulario };

    if (
      aux.A2_ZSENHA &&
      aux.A2_ZSENHA.length > 0 &&
      aux.A2_ZSENHA !== aux.A2_ZSENHA_CONFIRM
    ) {
      Swal.fire("Confirmação de senha não confere!");
      return;
    }

    if (!aux.A2_ZSENHA) {
      delete aux.A2_ZSENHA_CONFIRM;
      delete aux.A2_ZSENHA;
    }

    aux.A2_DTNASC = aux.A2_DTNASC?.replaceAll("-", "");
    aux.A2_CEP = aux.A2_CEP.replace(/\D/g, "");
    aux.A2_CGC = aux.A2_CGC.replace(/\D/g, "");

    Swal.fire({
      title: "Cadastro!",
      html: "Aguarde, salvando seus dados no sistema.",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    AuthFetchJson(url + "ALTERAPARCEIRO", {
      body: JSON.stringify({ dados: aux }),
      method: "POST",
    })
      .then(async (retorno: any) => {
        if (retorno.sucesso === "T") {
          await Promise.all(
            documentos.map((doc) =>
              AuthFetchJson(url + "ENVIADOCUMENTO", {
                method: "POST",
                body: JSON.stringify({
                  PARCEIRO: retorno.dados[0].A2_COD,
                  LOJA: retorno.dados[0].A2_LOJA,
                  ARQBASE64: `${doc.file}`.replace(/^.*,/, ""),
                  NOMEARQ: `${doc.name}`,
                }),
              }),
            ),
          )
            .then(() => {
              Swal.fire({
                position: "top-end",
                icon: "success",
                title: "Cadastro realizado com sucesso!",
                showConfirmButton: false,
                timer: 1500,
              });
            })
            .catch(() => {
              Swal.fire({
                position: "top-end",
                icon: "warning",
                title:
                  "Cadastro realizado com sucesso! Faça login para reenviar seus documentos ou entre em contato.",
                showConfirmButton: true,
              });
            });

          getDadosParceiro();
        } else {
          Swal.fire({
            position: "top-end",
            icon: "error",
            title: "Erro ao cadastrar cliente, " + retorno.mensagem,
            showConfirmButton: false,
            timer: 1500,
          });
        }
      })
      .catch((error) => {
        Swal.fire({
          position: "top-end",
          icon: "error",
          title: "Erro ao cadastrar cliente! " + error,
          showConfirmButton: false,
          timer: 1500,
        });
      })
      .finally(() => {
        if (Swal.isLoading()) Swal.close();
      });
  }

  /* ── Carga inicial dos dados ────────────────────────────── */
  function getDadosParceiro() {
    Swal.fire({
      title: "Meus Dados",
      html: "Aguarde enquanto carregamos seus dados.",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    const controller = new AbortController();

    AuthFetchJson(`${url}COLETAFORNECEDOR`, {
      signal: controller.signal,
      method: "POST",
      body: JSON.stringify({
        A2_COD: state.usuario.A2_COD,
        A2_LOJA: state.usuario.A2_LOJA,
      }),
    })
      .then((response) => {
        if (response.sucesso !== "T") {
          Swal.fire({
            title: "Meus Dados",
            html: "Houve um erro ao buscar seus dados. " + response.mensagem,
          });
          return;
        }
        response.dados[0].A2_CGC = Utils.maskaraCnpj(response.dados[0].A2_CGC);
        setFormulario(response.dados[0]);
      })
      .catch(() => {
        Swal.fire({
          title: "Meus Dados",
          html: "Houve um erro ao buscar seus dados.",
        });
      })
      .finally(() => {
        if (Swal.isLoading()) Swal.close();
      });

    return () => controller.abort();
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => getDadosParceiro(), []);

  const isLastTab = tab === "documentos";

  /* ── Render ─────────────────────────────────────────────── */
  return (
    <>
      <Menu />

      <div className={styles.container}>
        <div className={styles.content}>
          {/* Progress stepper */}
          <div className={styles.stepper}>
            {TABS.map((t, i) => {
              const isPast = currentStep > t.step;

              return (
                <div key={t.key} className={styles.stepperItem}>
                  <button
                    className={`
                    ${styles.stepCircle}
                    ${tab === t.key ? styles.stepActive : ""}
                    ${isPast ? styles.stepDone : ""}
                  `}
                    onClick={() => setTab(t.key)}
                    title={t.label}
                  >
                    {isPast ? "✓" : t.step}
                  </button>
                  <span
                    className={`${styles.stepLabel} ${tab === t.key ? styles.stepLabelActive : ""}`}
                    onClick={() => setTab(t.key)}
                  >
                    {t.label}
                  </span>
                  {i < TABS.length - 1 && (
                    <div
                      className={`${styles.stepLine} ${isPast ? styles.stepLineDone : ""}`}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Card */}
          <div className={styles.cardForm}>
            <div className={styles.cardHeader}>
              <h1>Minha Conta</h1>
              <div className={styles.cardHeaderRow}>
                <h2 className={styles.cardTitle}>
                  {TABS.find((t) => t.key === tab)?.label}
                </h2>
                <span className={styles.requiredNote}>
                  <RxDotFilled size={16} /> Campos Obrigatórios
                </span>
              </div>
            </div>

            <FormularioContext.Provider value={{ formulario, setFormulario }}>
              <ArquivosContext.Provider value={{ documentos, setDocumentos }}>
                {selectedTab()}
              </ArquivosContext.Provider>
            </FormularioContext.Provider>

            <hr className={styles.divider} />

            <div className={styles.actions}>
              {tab !== "dadosbasicos" && (
                <button className={styles.backSecBtn} onClick={handleBack}>
                  <FaArrowLeft size={16} /> Voltar
                </button>
              )}
              <button
                className={
                  isLastTab ? styles.nextBtnConfirmar : styles.nextBtnProximo
                }
                onClick={handleNext}
              >
                {isLastTab ? "Confirmar e finalizar" : "Próximo"}{" "}
                <FaArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
